import type { StructuredDocumentData } from "@/shared/types/documentuploadtype";
import type { TravelAdditionalInfoValues } from "../redux/type";
import { normalizeNationalityValue } from "./travelAdditionalInfo";

const additionalInfoOcrFieldMap = {
  nationality: ["Nationality", "Issuing_Country"],
  cityOfResidence: ["Place_of_Birth"],
  address: ["Address", "Passport_Address", "Address_Line_1"],
  emiratesId: ["Id Number", "Id_Number", "Emirates_Id", "Emirates ID"],
  passportNumber: ["Passport_Number", "Passport Number", "Passport_No", "Passport No"],
  beneficiaryName: ["Insured Name", "Name", "Full_Name", "Full Name"],
} satisfies Partial<Record<keyof TravelAdditionalInfoValues, string[]>>;

function getStructuredValue(structuredData: StructuredDocumentData, keys: string[]): string {
  const value = keys
    .map((key) => structuredData[key])
    .find((item) => typeof item === "string" && item.trim());

  return typeof value === "string" ? value.trim() : "";
}

function getStructuredGender(
  structuredData: StructuredDocumentData
): TravelAdditionalInfoValues["gender"] | "" {
  if (structuredData["Sex"] === "M" || structuredData["Gender"] === "Male") {
    return "Male";
  }

  if (structuredData["Sex"] === "F" || structuredData["Gender"] === "Female") {
    return "Female";
  }

  return "";
}

export function getTravelAdditionalInfoOcrDefaults(
  structuredData?: StructuredDocumentData
): Partial<TravelAdditionalInfoValues> {
  if (!structuredData) {
    return {};
  }

  const mappedDefaults = Object.entries(additionalInfoOcrFieldMap).reduce<
    Partial<TravelAdditionalInfoValues>
  >((defaults, [fieldName, ocrKeys]) => {
    const fieldKey = fieldName as keyof TravelAdditionalInfoValues;
    const value = getStructuredValue(structuredData, ocrKeys);

    if (!value) {
      return defaults;
    }

    return {
      ...defaults,
      [fieldKey]: fieldKey === "nationality" ? normalizeNationalityValue(value) : value,
    };
  }, {});

  const gender = getStructuredGender(structuredData);

  return gender
    ? {
        ...mappedDefaults,
        gender,
      }
    : mappedDefaults;
}

export function mergeTravelAdditionalInfoOcrDefaults(
  values: TravelAdditionalInfoValues,
  ocrDefaults: Partial<TravelAdditionalInfoValues>
): TravelAdditionalInfoValues {
  return Object.keys(ocrDefaults).reduce<TravelAdditionalInfoValues>((mergedValues, fieldName) => {
    const fieldKey = fieldName as keyof TravelAdditionalInfoValues;
    const currentValue = mergedValues[fieldKey];
    const ocrValue = ocrDefaults[fieldKey];

    if (typeof currentValue === "string" && !currentValue && ocrValue) {
      return {
        ...mergedValues,
        [fieldKey]: ocrValue,
      };
    }

    return mergedValues;
  }, values);
}
